import { InputPrefix, InputSuffix, TextBox } from '@progress/kendo-react-inputs';
import React, { useState } from 'react';
import { SvgIcon } from '@progress/kendo-react-common';
import { searchIcon, arrowLeftIcon } from '@progress/kendo-svg-icons';
import {
  useSearchContext,
  useDocumentsContext,
  useViewsContext,
  useGlobalizationContext,
  useTranslation,
  useGetView
} from '@cs-cms/state-management';
import { EMPTY_VALUE, CPS_FIELD_TYPE } from '@cs-cms/types';
import { GlobalSearchHelp } from './GlobalSearchHelp';

export const GlobalSearchFilter: React.FC = () => {
  const { queryRequest, setQueryRequest } = useSearchContext();
  const { setSelectedDocuments } = useDocumentsContext();
  const { views, selectedView } = useViewsContext();
  const { selectedLocale } = useGlobalizationContext();
  const [tempSearchPhrase, setTempSearchPhrase] = useState<string>(EMPTY_VALUE);
  const { translate } = useTranslation(selectedLocale);

  const defaultViewId = views?.find(view => view.isDefault)?.id;
  const searchViewId = selectedView ?? defaultViewId ?? views?.[0]?.id;
  const { data: searchView } = useGetView(searchViewId, selectedLocale);

  const handleSearch = (newSearchPhrase: string) => {
    if (!queryRequest || !setQueryRequest || !searchView?.fields) {
      return;
    }

    const trimmedSearchPhrase = newSearchPhrase.trim();

    setQueryRequest({
      ...queryRequest,
      searchFields: searchView.fields.filter(field => field.type !== CPS_FIELD_TYPE.DATE).map(field => field.name),
      searchText: trimmedSearchPhrase !== EMPTY_VALUE ? trimmedSearchPhrase : undefined
    });

    setTempSearchPhrase(trimmedSearchPhrase);
    if (setSelectedDocuments) {
      setSelectedDocuments([]);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch(tempSearchPhrase);
    }
  };

  return (
    <>
      {queryRequest && (
        <div className="globalSearchFilterWrapper">
          <TextBox
            id={'global-search-filter'}
            className="globalSearchFilter"
            placeholder={translate('custom.topBar.globalSearch')}
            defaultValue={queryRequest.searchText}
            value={tempSearchPhrase}
            onChange={e => setTempSearchPhrase(e.target.value !== undefined ? e.target.value?.toString() : '')}
            onKeyDown={handleKeyPress}
            fillMode={'outline'}
            autoComplete="off"
            prefix={() => (
              <>
                <InputPrefix>
                  <SvgIcon
                    className={tempSearchPhrase !== EMPTY_VALUE ? `globalSearchFilter--reset` : ''}
                    icon={tempSearchPhrase !== EMPTY_VALUE ? arrowLeftIcon : searchIcon}
                    onClick={() => handleSearch(EMPTY_VALUE)}
                  />
                </InputPrefix>
              </>
            )}
            suffix={() => (
              <>
                {tempSearchPhrase !== EMPTY_VALUE && (
                  <>
                    <InputSuffix>
                      <SvgIcon
                        className="globalSearchFilter--search"
                        enableBackground={1}
                        icon={searchIcon}
                        onClick={() => handleSearch(tempSearchPhrase)}
                      />
                    </InputSuffix>
                  </>
                )}
              </>
            )}
          />
          <GlobalSearchHelp />
        </div>
      )}
    </>
  );
};
